import {toMMSS} from '../utils/math/time';
import {TweenMax} from 'gsap';
import {emitter, Emitter, EVENTS} from '../utils/emitter';
import {PlayerIndicator} from '../views/components/playerIndicator/playerIndicator';
import {device} from '../utils/device';
import {getLanguage} from '../configs/global';
import {gtm} from '../utils/gtm';

export class VideoPlayer extends Emitter {
  public videoElement: HTMLVideoElement;
  public isPlaying: boolean;
  public fullScreen: boolean;
  private videoControls: HTMLElement;
  private playerIndicator: PlayerIndicator;
  private playTime: HTMLElement;
  private theaterModeButton: HTMLElement;
  private progressText: HTMLElement;
  private uiShowing: boolean;
  private canPause = false;
  private videoControlsParent: any;
  private subtitlesElement: HTMLElement;

  constructor(videoElement: HTMLVideoElement, videoControls: HTMLElement, subtitlesElement?: HTMLElement) {
    super();

    this.videoElement = videoElement;
    this.videoControls = videoControls;
    this.progressText = videoControls.querySelector('.progress');
    this.playTime = videoControls.querySelector('.playTime');
    this.theaterModeButton = videoControls.querySelector('.theaterMode');
    this.subtitlesElement = subtitlesElement;

    this.playerIndicator = new PlayerIndicator(videoControls.querySelector('.playerIndicatorWrapper'));
    this.playerIndicator.on(EVENTS.progress, this.onControllerProgress);

    this.addHandlers();

    if (device.isTouch() && this.subtitlesElement) this.subtitlesElement.style.display = `none`;

    emitter.on(EVENTS.fullScreen, this.onFullscreenChange.bind(this));
    emitter.on(EVENTS.keyEscape, this.onKeyEscape.bind(this));
  }

  private onKeyEscape() {
    if (this.fullScreen) {
      this.onFullScreenHandler();
    }
  }

  private onFullscreenChange() {
    if (!(document as any).webkitIsFullScreen && this.isPlaying) {
      this.pause();
    }
  }

  private onSubtitleChange = e => {
    const track = e.target;
    const text = (track.activeCues[0] && track.activeCues[0].text) || '';
    this.subtitlesElement.innerText = text;
    console.log(text);
    if (!device.isTouch()) track.mode = 'hidden';
  }

  private addHandlers() {
    this.theaterModeButton.addEventListener('click', this.onFullScreenHandler);
    this.videoElement.addEventListener('timeupdate', this.onPlayHandler);
    if (this.subtitlesElement) {
      this.videoElement.textTracks.length && this.videoElement.textTracks[0].addEventListener('cuechange', this.onSubtitleChange);
    }
  }

  private trackPlay = el => {
    gtm.trackVideo(el);
  };

  private playToggleHandler = () => {
    if (!this.isPlaying || device.isTouch()) {
      this.play();
      this.trackPlay(this.videoElement);
    } else {
      this.pause();
    }
  };

  private onControllerProgress = ({progress}) => {
    if (!this.canPause) return;
    this.videoElement.currentTime = this.videoElement.duration * progress;
    this.playerIndicator.setProgress(progress * 100);
  };

  private onMetaDataLoaded = () => {
    this.videoElement.removeEventListener('loadedmetadata', this.onMetaDataLoaded);
    this.videoElement.webkitEnterFullScreen();
  };

  private onPlayHandler = () => {
    this.playerIndicator.setProgress((this.videoElement.currentTime / this.videoElement.duration) * 100);
    if (this.videoElement.currentTime) {
      this.progressText.innerHTML = toMMSS(this.videoElement.currentTime);
    }
  };

  private onFullScreenHandler = () => {
    this.fullScreen = !this.fullScreen;
    let wrapper;

    let controls = this.videoControls;

    if (this.videoElement.closest('.carouselItem')) wrapper = this.videoElement.closest('.carouselItem');
    else wrapper = this.videoElement.closest('.wrapper');

    if (this.fullScreen) {
      document.body.classList.add('theaterModeEnabled');
      wrapper.classList.add('theaterModeWrapper');

      // WARNING: HUGE HACK TO AVOID BUG IN GSAP DRAGGABLE
      // - Take controls out and append to body in fullscreen mode.
      // - position fixed makes draggable freak out
      this.videoControlsParent = this.videoControls.parentNode;
      console.log(this.videoControlsParent);
      document.body.append(controls);
      controls.classList.add('theaterMode');
    } else {
      if (!this.isPlaying) {
        // this.videoElement.style.display = 'none';
        TweenMax.to([this.theaterModeButton, this.playTime], 0.4, {autoAlpha: 0});
      }
      document.body.classList.remove('theaterModeEnabled');
      wrapper.classList.remove('theaterModeWrapper');

      // WARNING: UNDO HUGE HACK
      controls.classList.remove('theaterMode');
      this.videoControlsParent.prepend(this.videoControls);
    }
  };

  private onMouseMoveHandler = () => {
    if (!this.uiShowing) {
      this.showUI();
      this.startAutoHideControls();
    }
  };

  private startAutoHideControls() {
    TweenMax.killDelayedCallsTo(this.hideUI);
    TweenMax.delayedCall(3, this.hideUI, null, this);
  }

  private _play(video: HTMLVideoElement) {
    const p = video.play();
    if (p !== undefined) {
      p.catch(e => {
        // console.log('cannot play', e);
      }).then(() => {
        this.canPause = true;
      });
    }
  }

  public play() {
    this.isPlaying = true;
    if (!device.isTouch()) {
      this.startAutoHideControls();
      this.videoElement.removeEventListener('mousemove', this.onMouseMoveHandler);
      this.videoElement.addEventListener('mousemove', this.onMouseMoveHandler);
      this.playerIndicator.showPause();
      if (!this.fullScreen) {
        TweenMax.to([this.theaterModeButton, this.playTime], 0.4, {autoAlpha: 1});
      }
      this._play(this.videoElement);
      this.emit(EVENTS.play, {});
    } else {
      this.emit(EVENTS.play, {});
      this.videoElement.load();
      this._play(this.videoElement);
      this.videoElement.addEventListener('loadedmetadata', this.onMetaDataLoaded);
    }
  }

  public pause() {
    if (!this.canPause) return;

    this.isPlaying = false;
    this.videoElement.removeEventListener('mousemove', this.onMouseMoveHandler);
    TweenMax.killDelayedCallsTo(this.hideUI);
    this.showUI();
    this.playerIndicator.showPlay();
    this.videoElement.pause();
    if (!this.fullScreen) {
      TweenMax.to([this.theaterModeButton, this.playTime], 0.4, {autoAlpha: 0});
    }

    this.emit(EVENTS.pause, {});
  }

  public showUI(delay = 0) {
    this.uiShowing = true;
    this.playerIndicator.hitarea.removeEventListener('click', this.playToggleHandler);
    TweenMax.to(this.videoControls, 0.4, {
      autoAlpha: 1,
      delay: delay,
      onComplete: () => {
        this.playerIndicator.hitarea.addEventListener('click', this.playToggleHandler);
      }
    });
  }

  public hideUI() {
    this.uiShowing = false;
    this.playerIndicator.hitarea.removeEventListener('click', this.playToggleHandler);
    TweenMax.to(this.videoControls, 0.4, {autoAlpha: 0});
  }

  public kill() {
    this.theaterModeButton.removeEventListener('click', this.onFullScreenHandler);
    this.videoElement.removeEventListener('mousemove', this.onMouseMoveHandler);
    this.videoElement.removeEventListener('timeupdate', this.onPlayHandler);
    this.playerIndicator.kill();
  }
}
